<script lang="ts" generics="T">
  import { ResponsiveVars } from '@twist/core';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import { cx } from '@emotion/css';
  import FontSizeDecorator from '$components/SanityComponents/PortableText/Decorators/FontSizeDecorator.svelte';

  interface Props<T> {
    tabs: Array<{
      id: T;
      label: string;
      ariaLabel?: string;
      ddActionName?: string;
    }>;
    activeTabId: T;
    onTabChange: (tabId: T) => void;
    ariaLabel: string;
  }

  let { tabs, activeTabId, onTabChange, ariaLabel }: Props<T> = $props();

  let tabElements: HTMLElement[] = $state([]);

  const responsiveVars = new ResponsiveVars().addFluid('padding', toFluidVarInput(9, 9)).toCSS();

  const handleTabFocus = (e: KeyboardEvent) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

    e.preventDefault();

    const currentIndex = tabElements.findIndex((el) => el === document.activeElement);
    if (currentIndex === -1) return;

    let newIndex;

    if (e.key === 'ArrowLeft') {
      newIndex = currentIndex === 0 ? tabs.length - 1 : currentIndex - 1;
    } else if (e.key === 'ArrowRight') {
      newIndex = currentIndex === tabs.length - 1 ? 0 : currentIndex + 1;
    }

    if (newIndex !== undefined && tabElements[newIndex]) {
      tabElements[newIndex].focus();
    }
  };
</script>

<div class={cx('tab-section', responsiveVars)}>
  <div class="tab-container" role="tablist" dir="ltr" aria-label={ariaLabel}>
    {#each tabs as tab, index}
      <button
        bind:this={tabElements[index]}
        id="{tab.id}-tab"
        class="tab-button"
        class:selected={activeTabId === tab.id}
        type="button"
        role="tab"
        aria-label={tab.ariaLabel}
        aria-selected={activeTabId === tab.id}
        aria-controls="{tab.id}-content"
        tabindex={activeTabId === tab.id ? -1 : 0}
        onclick={() => onTabChange(tab.id)}
        onkeydown={handleTabFocus}
        data-dd-action-name={tab.ddActionName}
      >
        <FontSizeDecorator>{tab.label}</FontSizeDecorator>
      </button>
    {/each}
  </div>
</div>

<style lang="scss">
  @import 'src/scss/variables';

  .tab-section {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  .tab-container {
    display: flex;
    justify-content: space-evenly;
    align-items: center;
    border: 1px solid $border-interactive-default-color;
    width: 100%;
    padding: var(--twist-core-spacing-2);
    gap: var(--twist-core-spacing-3);
    border-radius: 1000px;
  }

  .tab-button {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    border-radius: 1000px;
    border: none;
    background-color: $background-depth-0-color;
    cursor: pointer;
    font-family: var(--font-family-primary);
    padding: var(--padding);
    font-weight: 700;
    line-height: 120%;
    color: $text-default-color;
    transition: all 0.2s ease;
    appearance: none;

    &:focus {
      outline: none;
    }

    &.selected {
      color: $text-interactive-inverse-color;
      background-color: $background-inverse-color;
    }

    &:not(.selected) {
      color: $text-default-color;
      background: $background-depth-0-color;

      &:hover,
      &:focus {
        background-color: $background-depth-1-hover-color;
      }
    }
  }
</style>
